import React, { useState } from 'react';
import { Layout, Input, Button, Typography, Flex, theme, Space, Select, Spin, Grid, Empty, Tabs, Table, Tag, Modal, message, Card } from 'antd';
import { 
  LeftOutlined, 
  PlusOutlined,
  SearchOutlined,
  SortAscendingOutlined,
} from '@ant-design/icons';
import { Flow, ProcessFlow } from '../../../../types';
import { useWorkflows } from '../../../../hooks/useWorkflows';
import { ProcessFlowCard } from './ProcessFlowCard';
import { WorkflowDetail } from './WorkflowDetail';
import { STATUS_FILTERS, SORT_OPTIONS } from '../../../../constants';
import { formatTime, calculateDuration } from '../../../../utils';
import './AssistantProcessFlow.css';
import { useAccount } from '../../../../../../store';
import { useAssistant } from '../../../../../../contexts/AssistantContext';
import { useNavigate, useParams } from 'react-router-dom';
import { ProcessFlowAIGeneratorCard } from './ProcessFlowAIGeneratorCard';
import { AppBreadcrumb, useBreadcrumb } from '../../../../../breadcrumb';

import { createLogger } from '../../../../../../utils/logger';
const logger = createLogger('features-assistants');

const { Content } = Layout;
const { Title, Text, Paragraph, Link } = Typography;
const { Search } = Input;
const { useToken } = theme;
const { useBreakpoint } = Grid;

interface AssistantProcessFlowProps {
  onReturnButtonClick?: () => void;
  mode: 'create' | 'edit';
  flowData?: Flow;
  assistantId: string;
}

interface WorkflowsTabProps {
  workflows: ProcessFlow[];
  loading: boolean;
  error: string | null;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  statusFilter: string;
  setStatusFilter: (filter: string) => void;
  sortOrder: 'Most Recent' | 'Oldest';
  setSortOrder: (order: 'Most Recent' | 'Oldest') => void;
  onViewWorkflow: (workflow: ProcessFlow) => void;
  onActivate: (workflowId: string) => Promise<void>;
  onDeactivate: (workflowId: string) => Promise<void>;
  onDelete: (workflowId: string) => Promise<void>;
}

const WorkflowsTab: React.FC<WorkflowsTabProps> = ({ 
  workflows, 
  loading, 
  error, 
  searchQuery, 
  setSearchQuery, 
  statusFilter, 
  setStatusFilter, 
  sortOrder, 
  setSortOrder,
  onViewWorkflow,
  onActivate,
  onDeactivate,
  onDelete
}) => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const filteredAndSortedWorkflows = React.useMemo(() => {
    let filtered = workflows;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = workflows.filter(workflow => 
        workflow.name.toLowerCase().includes(query) || 
        workflow.description.toLowerCase().includes(query)
      );
    }

    if (statusFilter !== 'All') {
      filtered = filtered.filter(workflow => {
        switch (statusFilter) {
          case 'Active': return workflow.status === 'active';
          case 'Inactive': return workflow.status === 'inactive';
          default: return true;
        }
      });
    }

    return [...filtered].sort((a, b) => {
      const dateA = new Date(a.updatedAt).getTime();
      const dateB = new Date(b.updatedAt).getTime();
      return sortOrder === 'Most Recent' ? dateB - dateA : dateA - dateB;
    });
  }, [workflows, statusFilter, sortOrder, searchQuery]);

  const renderContent = () => {
    if (loading) {
      return (
        <Flex justify="center" align="center" style={{ minHeight: '200px' }}>
          <Spin size="large" />
        </Flex>
      );
    }

    if (error) {
      return (
        <Flex justify="center" align="center" style={{ minHeight: '200px' }}>
          <Text type="danger">{error}</Text>
        </Flex>
      );
    }

    if (filteredAndSortedWorkflows.length === 0) {
      return (
        <Flex justify="center" align="center" style={{ minHeight: '200px' }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Text type="secondary">
                {searchQuery || statusFilter !== 'All' 
                  ? 'No workflows match your search criteria'
                  : 'No workflows created yet'}
              </Text>
            }
          />
        </Flex>
      );
    }

    return (
      <Flex wrap className="process-flow-grid">
        {filteredAndSortedWorkflows.map((workflow) => (
          <Flex key={workflow.id} className="process-flow-card-container">
            <ProcessFlowCard
              workflow={workflow}
              onViewWorkflow={onViewWorkflow}
              onActivate={onActivate}
              onDeactivate={onDeactivate}
              onDelete={onDelete}
            />
          </Flex>
        ))}
      </Flex>
    );
  };

  return (
    <>
      <Flex gap="small" align="center" wrap className="process-flow-actions">
        <Input.Search
          placeholder="Search process flows..."
          className="process-flow-search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          allowClear
          style={{
            flex: 1,
            minWidth: '200px',
            width: isMobile ? '100%' : 'auto',
          }}
        />
        <Space wrap className="process-flow-filters">
          {STATUS_FILTERS.map(filter => (
            <Button 
              key={filter}
              type="default"
              onClick={() => setStatusFilter(filter)}
              style={{
                borderColor: statusFilter === filter ? 'var(--ant-colorSecondary)' : undefined,
                color: statusFilter === filter ? 'var(--ant-colorSecondary)' : undefined,
                backgroundColor: 'transparent'
              }}
            >
              {filter}
            </Button>
          ))}
        </Space>
        <Select
          value={sortOrder}
          style={{ width: 200 }}
          onChange={(value) => setSortOrder(value)}
          options={SORT_OPTIONS}
        />
      </Flex>
      
      <div className="process-flow-divider" />
      
      {renderContent()}
    </>
  );
};

const ExecutionsTab = ({ executions, loading, error, workflows }) => {
  const { token } = useToken();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [searchQuery, setSearchQuery] = React.useState('');

  const filteredExecutions = React.useMemo(() => {
    if (!searchQuery) return executions;
    const query = searchQuery.toLowerCase();
    return executions.filter(exec => {
      const runId = exec.id?.toLowerCase() || '';
      const flowName = (workflows.find(w => w.id === exec.workflowId)?.name || '').toLowerCase();
      const status = exec.status?.toLowerCase() || '';
      const triggeredBy = exec.input?.user_email?.toLowerCase() || '-';
      const startedAt = formatTime(exec.startedAt).toLowerCase();
      const duration = calculateDuration(exec.startedAt, exec.completedAt).toLowerCase();
      const allFields = `${runId} ${flowName} ${status} ${triggeredBy} ${startedAt} ${duration}`;
      return allFields.includes(query);
    });
  }, [executions, searchQuery, workflows]);

  const executionsColumns = [
    {
      title: 'Run ID',
      dataIndex: 'id',
      key: 'id',
      render: (id: string) => `#${id.slice(0, 6)}`,
    },
    {
      title: 'Flow name',
      dataIndex: 'workflowId',
      key: 'workflowId',
      render: (workflowId: string) => {
        const workflow = workflows.find(w => w.id === workflowId);
        return workflow?.name || workflowId;
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        let className = 'executions-status-tag default';
        if (status === 'running') className = 'executions-status-tag processing';
        if (status === 'success') className = 'executions-status-tag success';
        if (status === 'error') className = 'executions-status-tag error';
        return <Tag className={className}>{status}</Tag>;
      },
    },
    {
      title: 'Triggered by',
      dataIndex: 'input',
      key: 'triggeredBy',
      render: (input: any) => input?.user_email || '-',
    },
    {
      title: 'Started at',
      dataIndex: 'startedAt',
      key: 'startedAt',
      render: (date: string) => formatTime(date),
    },
    {
      title: 'Duration',
      key: 'duration',
      render: (record: any) => calculateDuration(record.startedAt, record.completedAt),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_: any, record: any) => (
        <>
          {record.status === 'error' && (
            <Button size="middle" style={{ marginRight: token.marginXS }}>
              Retry
            </Button>
          )}
          <Button size="middle">View</Button>
        </>
      ),
    },
  ];

  if (loading) {
    return (
      <Flex justify="center" align="center" style={{ minHeight: '200px' }}>
        <Spin size="large" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex justify="center" align="center" style={{ minHeight: '200px' }}>
        <Text type="danger">{error}</Text>
      </Flex>
    );
  }

  if (filteredExecutions.length === 0) {
    return (
      <>
        <Flex gap="small" align="center" wrap className="executions-actions">
          <Input.Search
            placeholder="Search executions..."
            className="executions-search"
            allowClear
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          <Space className="executions-buttons">
            <Button type="default">Export</Button>
            <Button type="default">Run multiple flows</Button>
          </Space>
        </Flex>
        <div className="process-flow-divider" />
        <Flex justify="center" align="center" style={{ minHeight: '200px' }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Text type="secondary">
                No executions found
              </Text>
            }
          />
        </Flex>
      </>
    );
  }

  return (
    <>
      <Flex gap="small" align="center" wrap className="executions-actions">
        <Input.Search
          placeholder="Search executions..."
          className="executions-search"
          allowClear
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
        <Space className="executions-buttons">
          <Button type="default">Export</Button>
          <Button type="default">Run multiple flows</Button>
        </Space>
      </Flex>
      
      <div className="process-flow-divider" />
      
      <Table
        columns={executionsColumns}
        dataSource={filteredExecutions}
        pagination={false}
        scroll={{ x: 800 }}
        className="executions-table"
      />
    </>
  );
};

export const AssistantProcessFlow: React.FC<AssistantProcessFlowProps> = ({ 
  onReturnButtonClick, 
  mode, 
  flowData, 
  assistantId 
}) => {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('All');
  const [sortOrder, setSortOrder] = useState<'Most Recent' | 'Oldest'>('Most Recent');
  const [activeTab, setActiveTab] = useState<string>('1');
  const [selectedWorkflow, setSelectedWorkflow] = useState<ProcessFlow | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [generatedWorkflow, setGeneratedWorkflow] = useState<string | undefined>(undefined);
  const { 
    workflows, 
    executions, 
    loading, 
    executionsLoading, 
    error,
    fetchExecutions,
    fetchWorkflows,
    handleActivateWorkflow,
    handleDeactivateWorkflow,
    handleDeleteWorkflow,
  } = useWorkflows(assistantId);

  const { currentAccount } = useAccount();
  const { currentAssistant } = useAssistant();
  const navigate = useNavigate();
  const { accountId } = useParams();

  React.useEffect(() => {
    if (activeTab === '2' && workflows.length > 0) {
      fetchExecutions(workflows[0].id);
    }
  }, [activeTab, workflows]);

  const handleViewWorkflow = (workflow: ProcessFlow) => {
    setSelectedWorkflow(workflow);
  };

  const handleBackToWorkflows = () => {
    setSelectedWorkflow(null);
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
    setGeneratedWorkflow(undefined);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setAiLoading(false);
    setGeneratedWorkflow(undefined);
  };

  const handleAIGenerate = async (prompt: string, name: string) => {
    setAiLoading(true);
    setGeneratedWorkflow(undefined);
    try {
      const apiBaseUrl = process.env.REACT_APP_API_HOST || 'https://api.tudominio.com/v1';
      const response = await fetch(`${apiBaseUrl}/v1/workflows`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          description: prompt,
          accountId: currentAccount?.id,
          assistantId
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.message || 'Error al generar workflow');
      }
      setGeneratedWorkflow(JSON.stringify(data, null, 2));
      message.success('Workflow generado correctamente');
      setIsModalOpen(false);
      fetchWorkflows(); // Refresca la lista de workflows
    } catch (e: any) {
      setGeneratedWorkflow('// Error al generar workflow');
      message.error(e?.message || 'Error al generar workflow');
    }
    setAiLoading(false);
  };

  const handleWorkflowGenerated = async (workflowJson: string, workflowName: string) => {
    setAiLoading(true);
    try {
      // Parse the workflow JSON to send to backend
      const workflowData = JSON.parse(workflowJson);

      const apiBaseUrl = process.env.REACT_APP_API_HOST || 'https://api.tudominio.com/v1';
      const response = await fetch(`${apiBaseUrl}/v1/workflows`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: workflowName,
          description: workflowData.name || workflowName,
          accountId: currentAccount?.id,
          assistantId,
          workflowData: workflowData // Send the full n8n workflow structure
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.message || 'Error saving workflow');
      }

      setGeneratedWorkflow(workflowJson);
      message.success(`Workflow "${workflowName}" created successfully!`);
      setIsModalOpen(false);
      fetchWorkflows(); // Refresh the workflows list
    } catch (e: any) {
      logger.error('Error saving workflow:', e);
      message.error(e?.message || 'Error saving workflow');
    } finally {
      setAiLoading(false);
    }
  };

  const breadcrumbItems = useBreadcrumb({
    section: 'Process Flows',
    includeAssistant: true,
    ...(selectedWorkflow && {
      additionalItems: [{
        label: selectedWorkflow.name,
        onClick: handleBackToWorkflows,
        isActive: true
      }]
    }),
    onSectionClick: selectedWorkflow ? handleBackToWorkflows : undefined
  });

  if (selectedWorkflow) {
    return (
      <>
        <AppBreadcrumb items={breadcrumbItems} />
        <WorkflowDetail
          workflow={selectedWorkflow}
          onBack={handleBackToWorkflows}
        />
      </>
    );
  }

  return (
    <>
      <AppBreadcrumb items={breadcrumbItems} />
      <Card>
        <Layout className="process-flow-container" key={assistantId}>
          <Content>
        <Flex vertical className="process-flow-content">
          <Flex justify="space-between" align="center" wrap className="process-flow-header">
            <Flex vertical gap="small">
              <Title level={2} className="process-flow-title">Process Flows</Title>
              <Paragraph type="secondary" className="process-flow-subtitle">
                Automate internal workflows by orchestrating decisions and streamlining complex operations.
              </Paragraph>
            </Flex>
            <Button 
              type="primary" 
              icon={<PlusOutlined />} 
              onClick={handleOpenModal}
              id="create-process-flow-button"
            >
              Create New Process Flow
            </Button>
          </Flex>

          <Tabs
            defaultActiveKey="1"
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key: '1',
                label: 'Workflows',
                children: (
                  <WorkflowsTab
                    workflows={workflows}
                    loading={loading}
                    error={error}
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    statusFilter={statusFilter}
                    setStatusFilter={setStatusFilter}
                    sortOrder={sortOrder}
                    setSortOrder={setSortOrder}
                    onViewWorkflow={handleViewWorkflow}
                    onActivate={handleActivateWorkflow}
                    onDeactivate={handleDeactivateWorkflow}
                    onDelete={handleDeleteWorkflow}
                  />
                ),
              },
              // {
              //   key: '2',
              //   label: 'Executions',
              //   children: (
              //     <ExecutionsTab
              //       executions={executions}
              //       loading={executionsLoading}
              //       error={error}
              //       workflows={workflows}
              //     />
              //   ),
              // },
            ]}
          />

          <Modal
            open={isModalOpen}
            onCancel={handleCloseModal}
            footer={null}
            width={600}
            title={null}
            destroyOnClose
          >
            <ProcessFlowAIGeneratorCard
              loading={aiLoading}
              onWorkflowGenerated={handleWorkflowGenerated}
              generatedWorkflow={generatedWorkflow}
            />
          </Modal>
        </Flex>
      </Content>
    </Layout>
    </Card>
    </>
  );
}; 